import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PageLoader } from '../components/common/LoadingOverlay';
import { MainLayout } from '../components/layout/MainLayout';
import { useAuth } from '../context/AuthContext';

const LoginPage = lazy(() => import('../pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('../pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('../pages/auth/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const EmployeesPage = lazy(() => import('../pages/employees/EmployeesPage').then((m) => ({ default: m.EmployeesPage })));
const EmployeeDetailPage = lazy(() => import('../pages/employees/EmployeeDetailPage').then((m) => ({ default: m.EmployeeDetailPage })));
const AttendancePage = lazy(() => import('../pages/attendance/AttendancePage').then((m) => ({ default: m.AttendancePage })));
const LeavePage = lazy(() => import('../pages/leave/LeavePage').then((m) => ({ default: m.LeavePage })));
const PayrollPage = lazy(() => import('../pages/payroll/PayrollPage').then((m) => ({ default: m.PayrollPage })));
const AssetsPage = lazy(() => import('../pages/assets/AssetsPage').then((m) => ({ default: m.AssetsPage })));
const ReportsPage = lazy(() => import('../pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const NotificationsPage = lazy(() => import('../pages/notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const JobsPage = lazy(() => import('../pages/jobs/JobsPage').then((m) => ({ default: m.JobsPage })));
const MasterDataPage = lazy(() => import('../pages/masters/MasterDataPage').then((m) => ({ default: m.MasterDataPage })));
const PlatformTenantsPage = lazy(() => import('../pages/platform/PlatformTenantsPage').then((m) => ({ default: m.PlatformTenantsPage })));
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const AuditLogsPage = lazy(() => import('../pages/audit/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })));
const HolidaysPage = lazy(() => import('../pages/holidays/HolidaysPage').then((m) => ({ default: m.HolidaysPage })));
const ShiftsPage = lazy(() => import('../pages/shifts/ShiftsPage').then((m) => ({ default: m.ShiftsPage })));
const WfhPage = lazy(() => import('../pages/wfh/WfhPage').then((m) => ({ default: m.WfhPage })));
const LoansPage = lazy(() => import('../pages/loans/LoansPage').then((m) => ({ default: m.LoansPage })));
const SalaryStructurePage = lazy(() => import('../pages/salary/SalaryStructurePage').then((m) => ({ default: m.SalaryStructurePage })));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

const PermissionRoute = ({ menuKey, children }) => {
  const { canAccessMenu, loading } = useAuth();

  if (loading) return <PageLoader />;
  if (!canAccessMenu(menuKey)) return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<PermissionRoute menuKey="dashboard"><DashboardPage /></PermissionRoute>} />
        <Route path="employees" element={<PermissionRoute menuKey="employees"><EmployeesPage /></PermissionRoute>} />
        <Route path="employees/:id" element={<PermissionRoute menuKey="employees"><EmployeeDetailPage /></PermissionRoute>} />
        <Route path="attendance" element={<PermissionRoute menuKey="attendance"><AttendancePage /></PermissionRoute>} />
        <Route path="holidays" element={<PermissionRoute menuKey="holidays"><HolidaysPage /></PermissionRoute>} />
        <Route path="shifts" element={<PermissionRoute menuKey="shifts"><ShiftsPage /></PermissionRoute>} />
        <Route path="leave" element={<PermissionRoute menuKey="leave"><LeavePage /></PermissionRoute>} />
        <Route path="wfh" element={<PermissionRoute menuKey="wfh"><WfhPage /></PermissionRoute>} />
        <Route path="payroll" element={<PermissionRoute menuKey="payroll"><PayrollPage /></PermissionRoute>} />
        <Route path="salary" element={<PermissionRoute menuKey="salary"><SalaryStructurePage /></PermissionRoute>} />
        <Route path="loans" element={<PermissionRoute menuKey="loans"><LoansPage /></PermissionRoute>} />
        <Route path="assets" element={<PermissionRoute menuKey="assets"><AssetsPage /></PermissionRoute>} />
        <Route path="reports" element={<PermissionRoute menuKey="reports"><ReportsPage /></PermissionRoute>} />
        <Route path="notifications" element={<PermissionRoute menuKey="notifications"><NotificationsPage /></PermissionRoute>} />
        <Route path="jobs" element={<PermissionRoute menuKey="jobs"><JobsPage /></PermissionRoute>} />
        <Route path="masters" element={<PermissionRoute menuKey="masters"><MasterDataPage /></PermissionRoute>} />
        <Route path="platform/tenants" element={<PermissionRoute menuKey="platform"><PlatformTenantsPage /></PermissionRoute>} />
        <Route path="settings" element={<PermissionRoute menuKey="settings"><SettingsPage /></PermissionRoute>} />
        <Route path="profile" element={<PermissionRoute menuKey="profile"><ProfilePage /></PermissionRoute>} />
        <Route path="audit-logs" element={<PermissionRoute menuKey="audit"><AuditLogsPage /></PermissionRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  </Suspense>
);

export default AppRoutes;
